// The home page. The server address leads; everything below helps a new player decide
// to join (news, this month's top players, how to start, what the server offers) and
// answers the questions people search for. Copy is shared with the server's crawler
// text in shared/seo-pages.json, so search engines read the same words players do.
import { Link } from "react-router-dom";
import seoPages from "../../../shared/seo-pages.json";
import { DiscordMark, Icon, type IconName } from "../icons";
import { formatNumber, useApi } from "../ui";
import { PlayerHead, formatRankingValue, type RankingKind } from "./minecraft";
import { CopyAddressButton, SERVER_ADDRESS, SiteShell } from "./SiteShell";
import { areaVisible, useArea, useSite } from "./context";

const home = seoPages.home;
const longDate = (value: string) => new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(new Date(value));

type ServerState = { data?: { online?: boolean; players?: { online?: number; max?: number } }; connect?: { bedrock?: { host: string; port: number } | null }; links?: { discord?: string | null } };

export function HomePage() {
  const status = useApi<ServerState>("/api/v1/public/minecraft/server");
  const { me } = useSite();
  const signedIn = Boolean(me.user);
  const show = { news: areaVisible(useArea("public.news"), signedIn), boards: areaVisible(useArea("public.leaderboards"), signedIn), vote: areaVisible(useArea("public.vote"), signedIn), join: areaVisible(useArea("public.join"), signedIn) };
  const discord = status.data?.links?.discord || null;
  const bedrock = status.data?.connect?.bedrock || null;
  return <SiteShell><main id="main">
    <section className="home-hero" aria-labelledby="home-title">
      <div className="container hero-grid">
        <div>
          <HeroStatus state={status}/>
          <h1 className="hero-title" id="home-title">SkyBlock, <span>the SkyBoxy way.</span></h1>
          <p className="hero-lead">{home.hero.lead}</p>
          <div className="address-card">
            <div><small>Server address · Java and Bedrock</small><strong>{SERVER_ADDRESS}</strong></div>
            <CopyAddressButton className="button primary" compactLabel={false} label="Copy IP"/>
          </div>
          <div className="hero-actions">
            {show.join && <Link className="button" to="/join"><Icon name="arrowRight" size={16}/>How to join</Link>}
            {discord && <a className="button discord" href={discord} target="_blank" rel="noopener noreferrer"><DiscordMark size={16}/>Join the Discord<span className="sr-only"> (opens in a new tab)</span></a>}
          </div>
        </div>
        <div className="hero-art"><img src="/assets/skyboxy-crest.webp" alt="The SkyBoxy island: a tree and a cottage on a floating block of land" width={460} height={460} fetchPriority="high"/></div>
      </div>
    </section>

    <section className="promises" aria-label="The SkyBoxy promise">
      <ul className="container">{(home.promises as Array<[IconName, string, string]>).map(([icon, title, text]) => <li key={title}>
        <Icon name={icon} size={22} aria-hidden="true"/><h2>{title}</h2><p>{text}</p>
      </li>)}</ul>
    </section>

    <section className="band" aria-labelledby="features-title">
      <div className="container">
        <div className="section-head"><div><h2 id="features-title">Built for the long grind</h2><p>Deep progression, fair rules, and a season long enough to see it all through.</p></div></div>
        <ul className="bento">{(home.features as Array<[IconName, string, string]>).map(([icon, title, text], index, all) => <li key={title} className={`${index < 2 || index === all.length - 1 ? "wide" : ""} ${index % 3 === 0 ? "gold" : ""}`}>
          <span className="tile-icon" aria-hidden="true"><Icon name={icon} size={20}/></span><h3>{title}</h3><p>{text}</p>
        </li>)}</ul>
        <p className="extras"><span>Also on SkyBoxy</span>{home.extras.map((extra) => <b key={extra}>{extra}</b>)}</p>
      </div>
    </section>

    {show.news && <LatestNews/>}
    {show.boards && <Champions/>}

    <section className="band raised" aria-labelledby="start-title">
      <div className="container">
        <div className="section-head"><div><h2 id="start-title">Your first evening on SkyBoxy</h2><p>No rush. Get settled, meet a few people, and build the island you want to play on.</p></div></div>
        <ol className="steps">{home.gettingStarted.map(([title, text]) => <li key={title}><h3>{title}</h3><p>{text}</p></li>)}</ol>
      </div>
    </section>

    {(show.vote || discord) && <CallsToAction discord={discord} showVote={show.vote}/>}

    <section className="band raised" aria-labelledby="faq-title">
      <div className="container faq">
        <div className="section-head"><div><h2 id="faq-title">Questions players ask</h2><p>Still stuck? Ask in the Discord and someone from the team will help.</p></div></div>
        <dl>{faqItems(bedrock, discord).map(([question, answer]) => <div key={question}><dt>{question}</dt><dd>{answer}</dd></div>)}</dl>
      </div>
    </section>
  </main></SiteShell>;
}

function HeroStatus({ state }: { state: { loading: boolean; data?: ServerState } }) {
  if (state.loading && !state.data) return <p className="hero-status"><span className="live-pulse paused" aria-hidden="true"/>Checking the server…</p>;
  const server = state.data?.data;
  if (!server?.online) return <p className="hero-status"><span className="live-pulse paused" aria-hidden="true"/>Server status unavailable right now</p>;
  return <p className="hero-status"><span className="live-pulse" aria-hidden="true"/><span><strong>{formatNumber(server.players?.online ?? 0)}</strong> {server.players?.online === 1 ? "player" : "players"} online now</span></p>;
}

// The same questions the server sends to search engines, with today's addresses filled in.
function faqItems(bedrock: { host: string; port: number } | null, discord: string | null) {
  const fill = (text: string) => text.replace("{java}", SERVER_ADDRESS).replace("{bedrock}", bedrock?.host || SERVER_ADDRESS).replace("{bedrockPort}", String(bedrock?.port || 19132));
  return home.faq.filter(([question]) => !(question.includes("Discord") && !discord)).map(([question, answer]) => [question, fill(answer)] as const);
}

type NewsItem = { slug: string; title: string; summary: string; coverUrl: string | null; publishedAt: string };

function LatestNews() {
  const state = useApi<{ items: NewsItem[] }>("/api/v1/public/news?limit=3");
  const items = state.data?.items || [];
  // News is optional: the section appears once the team has published something.
  if (!items.length) return null;
  return <section className="band" aria-labelledby="news-title">
    <div className="container">
      <div className="section-head"><div><h2 id="news-title">Latest news</h2></div><Link className="section-link" to="/news">All news<Icon name="arrowRight" size={16}/></Link></div>
      <div className="news-grid">{items.map((post, index) => <article key={post.slug} className="news-card">
        {index === 0 && post.coverUrl && <img src={post.coverUrl} alt="" loading="lazy"/>}
        <time dateTime={post.publishedAt}>{longDate(post.publishedAt)}</time>
        <h3><Link className="stretched" to={`/news/${post.slug}`}>{post.title}</Link></h3>
        {post.summary && <p>{post.summary}</p>}
        {index === 0 && <span className="news-more" aria-hidden="true">Read the post<Icon name="arrowRight" size={16}/></span>}
      </article>)}</div>
    </div>
  </section>;
}

const championBoards: Array<{ kind: RankingKind; endpoint: string; label: string; icon: IconName }> = [
  { kind: "islands", endpoint: "/api/v1/public/minecraft/islands", label: "Top island", icon: "island" },
  { kind: "balance", endpoint: "/api/v1/public/minecraft/economy", label: "Richest player", icon: "coin" },
  { kind: "playtime", endpoint: "/api/v1/public/minecraft/rankings/playtime", label: "Most play time", icon: "clock" },
];

function Champions() {
  const states = [useApi<any>(championBoards[0].endpoint), useApi<any>(championBoards[1].endpoint), useApi<any>(championBoards[2].endpoint)];
  const leaders = championBoards.map((board, index) => ({ ...board, data: states[index].data?.data, top: states[index].data?.data?.items?.[0] })).filter((board) => board.top);
  if (!leaders.length) return null;
  return <section className="band" aria-labelledby="champions-title">
    <div className="container">
      <div className="section-head"><div><h2 id="champions-title">Top of the leaderboards</h2><p>Live from the server. Think you can take a spot?</p></div><Link className="section-link" to="/minecraft/islandtop">Full leaderboards<Icon name="arrowRight" size={16}/></Link></div>
      <ul className="champions">{leaders.map((board) => <li key={board.kind} className="champion">
        <PlayerHead name={board.kind === "islands" ? board.top.owner?.name : board.top.name} size={52}/>
        <span><small>{board.label}</small><strong>{board.top.name}</strong></span>
        <b>{board.kind === "islands" ? `Level ${formatRankingValue(board.kind, board.top, board.data)}` : formatRankingValue(board.kind, board.top, board.data)}</b>
      </li>)}</ul>
    </div>
  </section>;
}

function CallsToAction({ discord, showVote }: { discord: string | null; showVote: boolean }) {
  return showVote ? <WithVoteSites discord={discord}/> : <GetInvolved discord={discord} hasVoteSites={false}/>;
}

function WithVoteSites({ discord }: { discord: string | null }) {
  const vote = useApi<{ sites: Array<{ name: string }> }>("/api/v1/public/vote");
  return <GetInvolved discord={discord} hasVoteSites={(vote.data?.sites || []).length > 0}/>;
}

function GetInvolved({ discord, hasVoteSites }: { discord: string | null; hasVoteSites: boolean }) {
  if (!hasVoteSites && !discord) return null;
  return <section className="band" aria-label="Get involved">
    <div className="container split-cta">
      {hasVoteSites && <article>
        <div><h2>Vote for SkyBoxy</h2><p>Server lists rank servers by votes. A vote takes a minute and helps new players find us.</p></div>
        <Link className="button primary" to="/vote">See voting sites</Link>
      </article>}
      {discord && <article>
        <div><h2>Join the Discord</h2><p>Events, giveaways, update notes, and help from the team all happen there.</p></div>
        <a className="button discord" href={discord} target="_blank" rel="noopener noreferrer"><DiscordMark size={16}/>Open Discord<span className="sr-only"> (opens in a new tab)</span></a>
      </article>}
    </div>
  </section>;
}
